import {
  BellAlertIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  HomeModernIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../../store/hook';
import type { CRMStats } from '../../../store/ModuleReducer';
import { setVigilanceStats } from '../../../store/ModuleReducer';
import type { RootState } from '../../../store/store';
import { getCompanyVigilancesStats } from '../../../utils/api';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Popup from '../../common/Popup/Popup.module';
import type { DueFilter, MeetingTabId, OwnerCommunicationTabId } from '../../Prospecting/utils/crmDeepLinks';
import { buildCrmLink } from '../../Prospecting/utils/crmDeepLinks';

type Due = Exclude<DueFilter, 'all'>;

interface NavigationChoice {
  title: string;
  description: string;
  options: Array<{ label: string; count?: number; onSelect: () => void }>;
}

const DUE_LABELS: Record<Due, string> = {
  late: 'en retard',
  today: 'aujourd\'hui',
  tomorrow: 'demain',
};

// ============================================================================
// Urgent Action Row Component - Main focus for quick visibility
// ============================================================================

interface ActionRowProps {
  label: string;
  icon: React.ReactNode;
  color: string;
  late: number | null;
  today: number | null;
  tomorrow: number | null;
  onClick: () => void;
  onCounterClick: (due: Due) => void;
  loading?: boolean;
}

function ActionRow({ label, icon, color, late, today, tomorrow, onClick, onCounterClick, loading = false }: ActionRowProps) {
  const hasLate = late !== null && late > 0;
  const hasToday = today !== null && today > 0;
  const hasTomorrow = tomorrow !== null && tomorrow > 0;
  const counterClass = 'flex min-w-[50px] cursor-pointer items-center justify-center gap-1 rounded-full px-2 py-1 text-xs font-semibold transition-all hover:ring-2 hover:ring-offset-1 focus:outline-none focus-visible:ring-2';
  const counterLabel = (due: Due, value: number | null) => `${label} : ${value ?? 0} ${DUE_LABELS[due]}`;

  return (
    <div className={`flex items-center gap-3 rounded-lg border p-2 transition-all hover:shadow-md ${hasLate ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
      {/* Label with icon */}
      <button
        type="button"
        className={`flex min-w-[140px] flex-1 cursor-pointer items-center gap-2 self-stretch text-left hover:underline ${color}`}
        onClick={onClick}
      >
        {icon}
        <span className="text-xs font-semibold tracking-wide uppercase">{label}</span>
      </button>

      {/* Badges */}
      <div className="flex items-center justify-end gap-2">
        {/* Late */}
        {loading ? (
          <Skeleton width={50} height={24} borderRadius={12} />
        ) : (
          <button
            type="button"
            className={`${counterClass} hover:ring-red-300 ${hasLate ? 'animate-pulse bg-red-500 text-white' : 'bg-red-100 text-red-400'}`}
            onClick={() => onCounterClick('late')}
            aria-label={counterLabel('late', late)}
            title={counterLabel('late', late)}
          >
            <ExclamationTriangleIcon className="h-3 w-3" />
            {late ?? 0}
          </button>
        )}

        {/* Today */}
        {loading ? (
          <Skeleton width={50} height={24} borderRadius={12} />
        ) : (
          <button
            type="button"
            className={`${counterClass} hover:ring-orange-300 ${hasToday ? 'bg-orange-500 text-white' : 'bg-orange-100 text-orange-400'}`}
            onClick={() => onCounterClick('today')}
            aria-label={counterLabel('today', today)}
            title={counterLabel('today', today)}
          >
            <BellAlertIcon className="h-3 w-3" />
            {today ?? 0}
          </button>
        )}

        {/* Tomorrow */}
        {loading ? (
          <Skeleton width={50} height={24} borderRadius={12} />
        ) : (
          <button
            type="button"
            className={`${counterClass} hover:ring-blue-300 ${hasTomorrow ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-400'}`}
            onClick={() => onCounterClick('tomorrow')}
            aria-label={counterLabel('tomorrow', tomorrow)}
            title={counterLabel('tomorrow', tomorrow)}
          >
            <CalendarDaysIcon className="h-3 w-3" />
            {tomorrow ?? 0}
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Collapsible Detail Section Component
// ============================================================================

interface DetailSectionProps {
  title: string;
  icon: React.ReactNode;
  color: string;
  children: React.ReactNode;
  total: number | null;
  loading?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onTitleClick: () => void;
}

function DetailSection({ title, icon, color, children, total, loading = false, isOpen, onToggle, onTitleClick }: DetailSectionProps) {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200">
      <div className={`flex w-full items-center ${color} text-white`}>
        <button
          type="button"
          className="flex flex-1 cursor-pointer items-center gap-2 py-2 pl-3 transition-opacity hover:opacity-90"
          onClick={onTitleClick}
          title={`Voir ${title}`}
        >
          {icon}
          <span className="text-xs font-semibold tracking-wide uppercase hover:underline">{title}</span>
          {loading ? (
            <span className="mr-2 ml-auto"><Skeleton width={24} height={16} baseColor="rgba(255,255,255,0.3)" highlightColor="rgba(255,255,255,0.5)" /></span>
          ) : (
            <span className="mr-2 ml-auto text-sm font-bold">{total ?? 0}</span>
          )}
        </button>
        <button
          type="button"
          className="cursor-pointer py-2 pr-3 pl-1 transition-opacity hover:opacity-80"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Masquer le détail' : 'Afficher le détail'}
        >
          {isOpen ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
        </button>
      </div>
      {isOpen && (
        <div className="flex-1 bg-gray-50 p-2">
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Stat Line Component (for detail sections)
// ============================================================================

interface StatLineProps {
  label: string;
  value: number | null;
  color?: string;
  loading?: boolean;
  onClick?: () => void;
}

function StatLine({ label, value, color = 'text-gray-700', loading = false, onClick }: StatLineProps) {
  const content = (
    <>
      <span className={`text-xs text-gray-600 ${onClick ? 'group-hover:text-rgpd-blue group-hover:underline' : ''}`}>{label}</span>
      {loading ? (
        <Skeleton width={24} height={14} />
      ) : (
        <span className={`text-xs font-semibold ${color}`}>{value ?? 0}</span>
      )}
    </>
  );

  if (!onClick) {
    return <div className="flex items-center justify-between px-2 py-1">{content}</div>;
  }

  return (
    <button
      type="button"
      className="group flex w-full cursor-pointer items-center justify-between rounded px-2 py-1 text-left hover:bg-white"
      onClick={onClick}
    >
      {content}
    </button>
  );
}

// ============================================================================
// Main CRM Summary Module
// ============================================================================

function CRMSummaryModule() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [crmStats, setCrmStats] = useState<CRMStats | null>(null);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  // Asks which list to open when a click covers several of them (RDV qualité, header badges).
  const [navigationChoice, setNavigationChoice] = useState<NavigationChoice | null>(null);
  const didAutoExpandForTodayRef = useRef(false);

  const { company, jwt } = useSelector((state: RootState) => ({
    company: state.auth.company,
    jwt: state.auth.token,
  }));

  const toggleDetails = () => setDetailsExpanded(!detailsExpanded);
  const toggleSection = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  };

  useEffect(() => {
    const fetchStats = async() => {
      if (!company?.id || !jwt) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(getCompanyVigilancesStats(company.id), {
          headers: {
            Authorization: JWTAuthorization(jwt),
          },
        });

        if (response.ok) {
          const data = await response.json();
          dispatch(setVigilanceStats(data));
          if (data.crm) {
            setCrmStats(data.crm);
          }
        }
      } catch (error) {
        console.error('Failed to fetch CRM stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [company?.id, jwt, dispatch]);

  useEffect(() => {
    didAutoExpandForTodayRef.current = false;
  }, [company?.id]);

  useEffect(() => {
    if (loading || !crmStats || didAutoExpandForTodayRef.current) {
      return;
    }
    didAutoExpandForTodayRef.current = true;
    const todaySum =
      crmStats.urgentActions.today +
      crmStats.urgentActions.meetingsToday +
      crmStats.transaction.meetingsToday +
      crmStats.gestionLocative.meetingsToday;
    if (todaySum > 1) {
      setIsExpanded(true);
    }
  }, [loading, crmStats]);

  const handleNavigateToNewOpportunities = (showNoOwner = false) => {
    navigate(ROUTES.PROSPECTING_NEW_OPPORTUNITIES, showNoOwner ? { state: { showNoOwner: true } } : undefined);
  };

  const handleNavigateToCommunicate = (tab?: OwnerCommunicationTabId, due?: Due) => {
    navigate(buildCrmLink(ROUTES.PROSPECTING_COMMUNICATE, { tab, due }));
  };

  const handleNavigateToTransaction = (tab?: MeetingTabId, due?: Due) => {
    navigate(buildCrmLink(ROUTES.PROSPECTING_TRANSACTION, { tab, due }));
  };

  const handleNavigateToRental = (tab?: MeetingTabId, due?: Due) => {
    navigate(buildCrmLink(ROUTES.PROSPECTING_RENTAL, { tab, due }));
  };

  // RDV qualité owners sit in « A contacter » of either Transaction or Gestion loc.: ask which one to open.
  const handleNavigateToQualityMeetings = (due?: Due) => {
    setNavigationChoice({
      title: `RDV qualité${due ? ` ${DUE_LABELS[due]}` : ''}`,
      description: 'Les propriétaires ayant un RDV qualité se trouvent dans l\'onglet « A contacter » de la transaction ou de la gestion locative. Quelle liste voulez-vous ouvrir ?',
      options: [
        { label: 'Transaction', onSelect: () => handleNavigateToTransaction('a-contacter', due) },
        { label: 'Gestion locative', onSelect: () => handleNavigateToRental('a-contacter', due) },
      ],
    });
  };

  // « Suivi des actions » rows: the label opens the list, a counter opens it filtered on that due date.
  const actionRows: Array<{ key: string; label: string; counts: Record<Due, number>; open: (due?: Due) => void }> = crmStats ? [
    {
      key: 'recalls',
      label: 'Rappels',
      counts: { late: crmStats.urgentActions.late, today: crmStats.urgentActions.today, tomorrow: crmStats.urgentActions.tomorrow },
      open: due => handleNavigateToCommunicate('demandes-rappel', due),
    },
    {
      key: 'quality',
      label: 'RDV qualité',
      counts: { late: 0, today: crmStats.urgentActions.meetingsToday, tomorrow: crmStats.urgentActions.meetingsTomorrow },
      open: due => handleNavigateToQualityMeetings(due),
    },
    {
      key: 'transaction',
      label: 'Transaction',
      counts: { late: crmStats.transaction.meetingsLate, today: crmStats.transaction.meetingsToday, tomorrow: crmStats.transaction.meetingsTomorrow },
      open: due => handleNavigateToTransaction('en-attente', due),
    },
    {
      key: 'rental',
      label: 'Gestion locative',
      counts: { late: crmStats.gestionLocative.meetingsLate, today: crmStats.gestionLocative.meetingsToday, tomorrow: crmStats.gestionLocative.meetingsTomorrow },
      open: due => handleNavigateToRental('en-attente', due),
    },
  ] : [];
  const openRow = (key: string, due?: Due) => actionRows.find(row => row.key === key)?.open(due);

  // Header badges sum every row: open the row directly when it is the only one, otherwise ask which one.
  const handleBadgeClick = (due: Due) => {
    const rows = actionRows.filter(row => row.counts[due] > 0);
    if (rows.length === 1) {
      rows[0].open(due);
      return;
    }
    setNavigationChoice({
      title: `Actions ${DUE_LABELS[due]}`,
      description: 'Quelle liste voulez-vous ouvrir ?',
      options: rows.map(row => ({ label: row.label, count: row.counts[due], onSelect: () => row.open(due) })),
    });
  };

  if (!crmStats && !loading) {
    return null;
  }

  // Calculate totals for urgent badge in header
  const totalLate = crmStats ? (
    crmStats.urgentActions.late +
    crmStats.transaction.meetingsLate +
    crmStats.gestionLocative.meetingsLate
  ) : 0;

  const totalToday = crmStats ? (
    crmStats.urgentActions.today +
    crmStats.urgentActions.meetingsToday +
    crmStats.transaction.meetingsToday +
    crmStats.gestionLocative.meetingsToday
  ) : 0;

  return (
    <div className="relative overflow-hidden rounded-xl bg-white shadow-md">
      <div className="absolute top-0 right-0 left-0 h-1 rounded-t-xl bg-gradient-to-r from-rgpd-blue to-rgpd-green" />

      <div className="absolute top-4 right-5 z-10">
        <button
          onClick={toggleSection}
          aria-expanded={isExpanded}
          aria-controls="crm-summary-section"
          className="inline-flex items-center gap-1.5 rounded-lg bg-rgpd-blue px-3 py-1.5 text-xs font-medium text-white transition-all duration-200 hover:bg-rgpd-blue-dark"
        >
          <UserGroupIcon className="h-3.5 w-3.5" />
          <span>{isExpanded ? 'Réduire' : 'Étendre'}</span>
          {isExpanded ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />}
        </button>
      </div>

      <div className="p-6">
        <div className="mb-4 flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-rgpd-blue/10 text-rgpd-blue">
              <UserGroupIcon className="h-6 w-6" />
            </div>
            <div className="pr-28">
              <h2 className="text-lg leading-snug font-semibold text-gray-800">Suivi CRM - Dirigeant cabinet : Suivi des actions</h2>
              <p className="mt-0.5 text-xs text-gray-500">Actions à traiter en un coup d&apos;oeil</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2 pr-28">
            {!loading && totalLate > 0 && (
              <button
                type="button"
                onClick={() => handleBadgeClick('late')}
                className="flex animate-pulse cursor-pointer items-center gap-1.5 rounded-full bg-red-500 px-3 py-1.5 text-white transition-all hover:ring-2 hover:ring-red-300 hover:ring-offset-1"
              >
                <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold">{totalLate} en retard</span>
              </button>
            )}
            {!loading && totalToday > 0 && (
              <button
                type="button"
                onClick={() => handleBadgeClick('today')}
                className="flex cursor-pointer items-center gap-1.5 rounded-full bg-orange-500 px-3 py-1.5 text-white transition-all hover:ring-2 hover:ring-orange-300 hover:ring-offset-1"
              >
                <BellAlertIcon className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold">{totalToday} aujourd&apos;hui</span>
              </button>
            )}
            {loading && (
              <>
                <Skeleton width={90} height={26} borderRadius={20} />
                <Skeleton width={110} height={26} borderRadius={20} />
              </>
            )}
          </div>
        </div>

        <div id="crm-summary-section" className={`transition-all duration-500 ease-in-out ${
          isExpanded ? 'max-h-[2000px] overflow-visible opacity-100' : 'max-h-0 overflow-hidden opacity-0'
        }`}>
          {/* ================================================================ */}
          {/* URGENT ACTIONS GRID - Main focus area                           */}
          {/* ================================================================ */}
          <div className="mb-4">
            {/* Column headers */}
            <div className="mb-2 flex items-center gap-3 px-2">
              <div className="min-w-[140px]" />
              <div className="flex flex-1 items-center justify-end gap-2">
                <div className="flex min-w-[50px] items-center justify-center gap-1 text-xs font-semibold text-red-600">
                  <ExclamationTriangleIcon className="h-3 w-3" />
                  Retard
                </div>
                <div className="flex min-w-[50px] items-center justify-center gap-1 text-xs font-semibold text-orange-600">
                  <BellAlertIcon className="h-3 w-3" />
                  Auj.
                </div>
                <div className="flex min-w-[50px] items-center justify-center gap-1 text-xs font-semibold text-blue-600">
                  <CalendarDaysIcon className="h-3 w-3" />
                  Demain
                </div>
              </div>
            </div>

            {/* Action rows */}
            <div className="space-y-2">
              <ActionRow
                label="Rappels"
                icon={<UserGroupIcon className="h-4 w-4" />}
                color="text-gray-700"
                late={crmStats?.urgentActions.late ?? null}
                today={crmStats?.urgentActions.today ?? null}
                tomorrow={crmStats?.urgentActions.tomorrow ?? null}
                onClick={() => openRow('recalls')}
                onCounterClick={due => openRow('recalls', due)}
                loading={loading}
              />
              <ActionRow
                label="RDV Qualité"
                icon={<UserGroupIcon className="h-4 w-4" />}
                color="text-gray-700"
                late={0}
                today={crmStats?.urgentActions.meetingsToday ?? null}
                tomorrow={crmStats?.urgentActions.meetingsTomorrow ?? null}
                onClick={() => openRow('quality')}
                onCounterClick={due => openRow('quality', due)}
                loading={loading}
              />
              <ActionRow
                label="Transaction"
                icon={<HomeModernIcon className="h-4 w-4" />}
                color="text-rgpd-blue"
                late={crmStats?.transaction.meetingsLate ?? null}
                today={crmStats?.transaction.meetingsToday ?? null}
                tomorrow={crmStats?.transaction.meetingsTomorrow ?? null}
                onClick={() => openRow('transaction')}
                onCounterClick={due => openRow('transaction', due)}
                loading={loading}
              />
              <ActionRow
                label="Gestion Loc."
                icon={<HomeModernIcon className="h-4 w-4" />}
                color="text-rgpd-blue"
                late={crmStats?.gestionLocative.meetingsLate ?? null}
                today={crmStats?.gestionLocative.meetingsToday ?? null}
                tomorrow={crmStats?.gestionLocative.meetingsTomorrow ?? null}
                onClick={() => openRow('rental')}
                onCounterClick={due => openRow('rental', due)}
                loading={loading}
              />
            </div>
          </div>

          {/* ================================================================ */}
          {/* DETAILED STATS - Collapsible sections (all expand/collapse together) */}
          {/* ================================================================ */}
          <div className="mb-4 grid grid-cols-1 items-stretch gap-2 md:grid-cols-2 lg:grid-cols-4">
            {/* DPE Stats */}
            <DetailSection
              title="DPE"
              icon={<DocumentTextIcon className="h-4 w-4" />}
              color="bg-slate-600"
              total={crmStats?.dpe.total ?? null}
              loading={loading}
              isOpen={detailsExpanded}
              onToggle={toggleDetails}
              onTitleClick={() => handleNavigateToNewOpportunities()}
            >
              <StatLine label="Nouveaux" value={crmStats?.dpe.new ?? null} color="text-blue-600" loading={loading} onClick={() => handleNavigateToNewOpportunities()} />
              <StatLine label="Sans propriétaire" value={crmStats?.dpe.noOwner ?? null} color="text-gray-500" loading={loading} onClick={() => handleNavigateToNewOpportunities(true)} />
              <StatLine label="Identifiés" value={crmStats?.dpe.identified ?? null} color="text-purple-600" loading={loading} />
              <StatLine label="En cours" value={crmStats?.dpe.inProcess ?? null} color="text-orange-500" loading={loading} />
              <StatLine label="Terminés" value={crmStats?.dpe.processed ?? null} color="text-green-600" loading={loading} />
              <div className="mt-2 border-t border-gray-200 pt-2">
                <button
                  className="w-full text-xs text-rgpd-blue hover:underline"
                  onClick={(e) => { e.stopPropagation(); handleNavigateToNewOpportunities(); }}
                >
                  Voir les DPE →
                </button>
              </div>
            </DetailSection>

            {/* Owner Stats */}
            <DetailSection
              title="Propriétaires"
              icon={<UserGroupIcon className="h-4 w-4" />}
              color="bg-slate-600"
              total={crmStats?.owners.total ?? null}
              loading={loading}
              isOpen={detailsExpanded}
              onToggle={toggleDetails}
              onTitleClick={() => handleNavigateToCommunicate()}
            >
              <StatLine label="Non contactés" value={crmStats?.owners.notContacted ?? null} color="text-blue-600" loading={loading} onClick={() => handleNavigateToCommunicate('non-contacte')} />
              <StatLine label="En attente" value={crmStats?.owners.waitingForFeedback ?? null} color="text-yellow-600" loading={loading} onClick={() => handleNavigateToCommunicate('en-attente')} />
              <StatLine label="A rappeler" value={crmStats?.owners.toRecall ?? null} color="text-orange-500" loading={loading} onClick={() => handleNavigateToCommunicate('demandes-rappel')} />
              <StatLine label="A appeler" value={crmStats?.owners.toCall ?? null} color="text-purple-600" loading={loading} onClick={() => handleNavigateToCommunicate('a-appeler')} />
              <StatLine label="Injoignables" value={crmStats?.owners.unableToReach ?? null} color="text-gray-500" loading={loading} />
              <div className="mt-2 border-t border-gray-200 pt-2">
                <button
                  className="w-full text-xs text-rgpd-blue hover:underline"
                  onClick={(e) => { e.stopPropagation(); handleNavigateToCommunicate(); }}
                >
                  Voir les propriétaires →
                </button>
              </div>
            </DetailSection>

            {/* Transaction Stats */}
            <DetailSection
              title="Transaction"
              icon={<HomeModernIcon className="h-4 w-4" />}
              color="bg-rgpd-blue"
              total={crmStats?.transaction.total ?? null}
              loading={loading}
              isOpen={detailsExpanded}
              onToggle={toggleDetails}
              onTitleClick={() => handleNavigateToTransaction()}
            >
              <StatLine label="A contacter" value={crmStats?.transaction.toContact ?? null} color="text-blue-600" loading={loading} onClick={() => handleNavigateToTransaction('a-contacter')} />
              <StatLine label="En attente RDV" value={crmStats?.transaction.waitingForMeeting ?? null} color="text-orange-500" loading={loading} onClick={() => handleNavigateToTransaction('en-attente')} />
              <StatLine label="Refus" value={crmStats?.transaction.refused ?? null} color="text-gray-500" loading={loading} onClick={() => handleNavigateToTransaction('refus')} />
              <StatLine label="Clôturés" value={crmStats?.transaction.closed ?? null} color="text-green-600" loading={loading} onClick={() => handleNavigateToTransaction('suivi-cloture')} />
              <div className="mt-2 border-t border-gray-200 pt-2">
                <button
                  className="w-full text-xs text-rgpd-blue hover:underline"
                  onClick={(e) => { e.stopPropagation(); handleNavigateToTransaction(); }}
                >
                  Voir Transaction →
                </button>
              </div>
            </DetailSection>

            {/* Gestion Locative Stats */}
            <DetailSection
              title="Gestion Loc."
              icon={<HomeModernIcon className="h-4 w-4" />}
              color="bg-rgpd-blue"
              total={crmStats?.gestionLocative.total ?? null}
              loading={loading}
              isOpen={detailsExpanded}
              onToggle={toggleDetails}
              onTitleClick={() => handleNavigateToRental()}
            >
              <StatLine label="A contacter" value={crmStats?.gestionLocative.toContact ?? null} color="text-blue-600" loading={loading} onClick={() => handleNavigateToRental('a-contacter')} />
              <StatLine label="En attente RDV" value={crmStats?.gestionLocative.waitingForMeeting ?? null} color="text-orange-500" loading={loading} onClick={() => handleNavigateToRental('en-attente')} />
              <StatLine label="Refus" value={crmStats?.gestionLocative.refused ?? null} color="text-gray-500" loading={loading} onClick={() => handleNavigateToRental('refus')} />
              <StatLine label="Clôturés" value={crmStats?.gestionLocative.closed ?? null} color="text-green-600" loading={loading} onClick={() => handleNavigateToRental('suivi-cloture')} />
              <div className="mt-2 border-t border-gray-200 pt-2">
                <button
                  className="w-full text-xs text-rgpd-blue hover:underline"
                  onClick={(e) => { e.stopPropagation(); handleNavigateToRental(); }}
                >
                  Voir Gestion Locative →
                </button>
              </div>
            </DetailSection>
          </div>
        </div>
      </div>

      <Popup
        isOpen={navigationChoice !== null}
        onClose={() => setNavigationChoice(null)}
        title={navigationChoice?.title}
        additionalClass="w-full max-w-md"
      >
        <p className="mb-4 text-sm text-gray-600">{navigationChoice?.description}</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {navigationChoice?.options.map(option => (
            <button
              key={option.label}
              type="button"
              className="flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-rgpd-blue px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-rgpd-blue-dark"
              onClick={() => {
                setNavigationChoice(null);
                option.onSelect();
              }}
            >
              {option.label}
              {option.count !== undefined && (
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">{option.count}</span>
              )}
            </button>
          ))}
        </div>
      </Popup>
    </div>
  );
}

export default CRMSummaryModule;
